import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Plus, Pencil, Trash2, X, Package } from "lucide-react";

import { API_URL as API } from "../../config";
const CATEGORIES = ["Cleats", "Jerseys", "Shorts", "Balls", "Cones", "Bibs", "Shin Guards", "Gloves", "Goals", "Bags", "Other"];
const CONDITIONS = ["new", "good", "worn", "needs-replacement"];
const emptyForm = { item_name: "", category: "", quantity: "1", condition: "good", assigned_to: "", notes: "" };

export default function GearPage() {
  const { authFetch } = useAuth();
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [filter, setFilter] = useState("");
  const a = t.admin;

  const load = async () => {
    const [gearRes, athRes] = await Promise.all([
      authFetch(`${API}/api/admin/gear`),
      authFetch(`${API}/api/admin/athletes`),
    ]);
    if (gearRes.ok) setItems(await gearRes.json());
    if (athRes.ok) setAthletes(await athRes.json());
  };

  useEffect(() => { load(); }, [authFetch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const body = { ...form, quantity: parseInt(form.quantity) || 1 };
    Object.keys(body).forEach(k => { if (body[k] === "") delete body[k]; });
    if (!body.item_name || !body.category) return;
    const url = editId ? `${API}/api/admin/gear/${editId}` : `${API}/api/admin/gear`;
    await authFetch(url, { method: editId ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    setShowForm(false);
    setForm(emptyForm);
    setEditId(null);
    load();
  };

  const handleEdit = (item) => {
    setForm({ item_name: item.item_name || "", category: item.category || "", quantity: item.quantity?.toString() || "1", condition: item.condition || "good", assigned_to: item.assigned_to || "", notes: item.notes || "" });
    setEditId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await authFetch(`${API}/api/admin/gear/${id}`, { method: "DELETE" });
    load();
  };

  const athleteMap = {};
  athletes.forEach(at => { athleteMap[at.id] = at.name; });

  const filtered = filter ? items.filter(i => i.category === filter) : items;
  const conditionColors = { new: "bg-blue-100 text-blue-700", good: "bg-emerald-100 text-emerald-700", worn: "bg-amber-100 text-amber-700", "needs-replacement": "bg-red-100 text-red-700" };

  return (
    <div data-testid="gear-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 font-['Kanit']">{a.gearInventory}</h2>
          <p className="text-gray-500 text-sm">{items.length} {a.itemsTracked}</p>
        </div>
        <button onClick={() => { setForm(emptyForm); setEditId(null); setShowForm(true); }} data-testid="add-gear-btn"
          className="flex items-center gap-2 bg-[#87CEEB] hover:bg-[#6bb8d9] text-black px-5 py-2.5 rounded-xl font-semibold text-sm transition-all">
          <Plus className="w-4 h-4" /> {a.addItem}
        </button>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={() => setFilter("")} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${!filter ? "bg-[#87CEEB] text-black" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{a.all}</button>
        {CATEGORIES.map(cat => (
          <button key={cat} onClick={() => setFilter(cat)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filter === cat ? "bg-[#87CEEB] text-black" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{cat}</button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-semibold text-gray-600">{a.item}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden sm:table-cell">{a.category}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">{a.qty}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">{a.condition}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden md:table-cell">{a.assignedTo}</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-600">{a.actions}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-12 text-center text-gray-400">
                  <Package className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  {items.length === 0 ? a.noGearItems : a.noItemsCategory}
                </td></tr>
              ) : filtered.map(item => (
                <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`gear-row-${item.id}`}>
                  <td className="px-4 py-3 font-medium text-gray-900">{item.item_name}</td>
                  <td className="px-4 py-3 text-gray-600 hidden sm:table-cell">{item.category}</td>
                  <td className="px-4 py-3 text-gray-600">{item.quantity}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${conditionColors[item.condition] || "bg-gray-100 text-gray-600"}`}>
                      {item.condition}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{athleteMap[item.assigned_to] || item.assigned_to || "-"}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => handleEdit(item)} className="p-1.5 hover:bg-blue-50 rounded-lg text-blue-500 mr-1"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(item.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-red-500"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md" data-testid="gear-form-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-bold font-['Kanit']">{editId ? a.editItem : a.addGearItem}</h3>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{a.itemName} *</label>
                <input value={form.item_name} onChange={e => setForm({...form, item_name: e.target.value})} required
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" data-testid="gear-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{a.category} *</label>
                  <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} required
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" data-testid="gear-category-select">
                    <option value="">Select...</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{a.qty}</label>
                  <input type="number" min="0" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" data-testid="gear-quantity-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{a.condition}</label>
                  <select value={form.condition} onChange={e => setForm({...form, condition: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" data-testid="gear-condition-select">
                    {CONDITIONS.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{a.assignedTo}</label>
                  <select value={form.assigned_to} onChange={e => setForm({...form, assigned_to: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]">
                    <option value="">{a.unassigned}</option>
                    {athletes.map(at => <option key={at.id} value={at.id}>{at.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB] resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)}
                  className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">{a.cancel}</button>
                <button type="submit" data-testid="gear-form-submit"
                  className="flex-1 py-2.5 bg-[#87CEEB] hover:bg-[#6bb8d9] text-black rounded-xl text-sm font-bold">{editId ? a.update : a.addItem}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
